import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";

const TYPEFORM_URL = "https://form.typeform.com/to/yXF9HCS0";
const DISCORD_INVITE = "https://discord.gg/8rhhsY8aHb";

/* ---------- Icons ---------- */

function YouTubeIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M23.498 6.186a2.999 2.999 0 0 0-2.112-2.124C19.557 3.5 12 3.5 12 3.5s-7.557 0-9.386.562A2.999 2.999 0 0 0 .502 6.186 31.46 31.46 0 0 0 0 12a31.46 31.46 0 0 0 .502 5.814 2.999 2.999 0 0 0 2.112 2.124C4.443 20.5 12 20.5 12 20.5s7.557 0 9.386-.562a2.999 2.999 0 0 0 2.112-2.124A31.46 31.46 0 0 0 24 12a31.46 31.46 0 0 0-.502-5.814ZM9.75 15.568V8.432L15.818 12 9.75 15.568Z" />
    </svg>
  );
}
function TikTokIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.95a8.16 8.16 0 0 0 4.77 1.52V7.03a4.85 4.85 0 0 1-1.84-.34z" />
    </svg>
  );
}
function InstagramIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}
function DiscordIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.07.07 0 0 0-.074.035c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0c-.164-.39-.405-.875-.617-1.25a.077.077 0 0 0-.074-.035A19.74 19.74 0 0 0 3.687 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.292a.074.074 0 0 1 .077-.01c3.927 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.009c.12.1.246.198.373.292a.077.077 0 0 1-.006.128 12.298 12.298 0 0 1-1.873.892.077.077 0 0 0-.04.106c.36.698.772 1.362 1.225 1.994a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.548-13.66a.061.061 0 0 0-.031-.028ZM8.02 15.331c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.42-2.157 2.42Zm7.974 0c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.42-2.157 2.42Z" />
    </svg>
  );
}

/* ---------- Rotating platform card ---------- */

const PLATFORMS = [
  { name: "YouTube",   color: "#ff0033", Icon: YouTubeIcon },
  { name: "TikTok",    color: "#22d3ee", Icon: TikTokIcon },
  { name: "Instagram", color: "#E1306C", Icon: InstagramIcon },
];

function RotatingPlatform() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % PLATFORMS.length), 1500);
    return () => clearInterval(id);
  }, []);
  const p = PLATFORMS[idx];
  // Outer card stays still — only the icon + label fade-up on each change.
  // Thinner outline + much stronger ambient glow in platform color.
  return (
      <div className="mt-9 inline-flex">
        <div
          data-testid="cb-rotating-platform"
          className="inline-flex items-center gap-3.5 rounded-[28px] border bg-[#0a0a0c] px-9 py-5 transition-[border-color,box-shadow] duration-500"
          style={{
            borderColor: `${p.color}40`,
            boxShadow: `0 0 70px 6px ${p.color}33, 0 0 28px ${p.color}55, 0 0 0 1px ${p.color}33 inset`,
          }}
        >
        <span key={`icon-${p.name}`} className="cb-plat-in flex items-center" style={{ color: p.color }}>
          <p.Icon size={36} />
        </span>
        <span
          key={`label-${p.name}`}
          className="cb-plat-in cb-display text-4xl sm:text-5xl"
          style={{ color: p.color }}
        >
          {p.name}
        </span>
      </div>
    </div>
  );
}

/* ---------- Video Slider ---------- */

const VIDEOS = [
  {
    src: "https://customer-assets.emergentagent.com/job_a0f2bc98-1777-47fa-af89-b70f5f00913d/artifacts/sjshzncj_snaptik_7642226789208706317_v3.mp4",
    platform: "TikTok",
    href: "https://www.tiktok.com",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_a0f2bc98-1777-47fa-af89-b70f5f00913d/artifacts/wtsp4gkd_snaptik_7642587960755440910_v3.mp4",
    platform: "TikTok",
    href: "https://www.tiktok.com",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_a0f2bc98-1777-47fa-af89-b70f5f00913d/artifacts/n20lojac_snaptik_7641816448859507975_v3.mp4",
    platform: "TikTok",
    href: "https://www.tiktok.com",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_a0f2bc98-1777-47fa-af89-b70f5f00913d/artifacts/ol6ouey0_BunnaB%20Ordered%20The%20Whole%20Menu%21%F0%9F%98%AD%F0%9F%92%80%20-%20StreamerStars%20%281080p%2C%20h264%29.mp4",
    platform: "YouTube",
    href: "https://www.youtube.com/shorts",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_a0f2bc98-1777-47fa-af89-b70f5f00913d/artifacts/sjshzncj_snaptik_7642226789208706317_v3.mp4",
    platform: "YouTube",
    href: "https://www.youtube.com/shorts",
  },
];

function VideoCard({ video, idx }) {
  const Icon = video.platform === "TikTok" ? TikTokIcon : YouTubeIcon;
  return (
    <a
      href={video.href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`cb-video-card-${idx}`}
      className="group relative mx-2 block w-[220px] flex-shrink-0 overflow-hidden rounded-2xl border border-[#1f1f22] bg-black sm:w-[240px]"
      style={{ aspectRatio: "9 / 16" }}
    >
      <video
        src={video.src}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className="h-full w-full object-cover"
      />
      <div className="absolute left-2.5 top-2.5 inline-flex items-center gap-1.5 rounded-full bg-black/75 px-2.5 py-1.5 text-[11px] font-bold text-white backdrop-blur">
        <Icon size={12} />
        {video.platform}
      </div>
      <div className="pointer-events-none absolute right-2.5 top-2.5 rounded-full bg-black/75 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100">
        <ExternalLink size={13} />
      </div>
    </a>
  );
}

function VideoSlider() {
  const loop = [...VIDEOS, ...VIDEOS];
  return (
    <section data-testid="cb-video-slider" className="bg-[#050505] pb-12 pt-4 sm:pt-6">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="cb-marquee-mask overflow-hidden">
          <div className="cb-marquee-track cb-marquee-50">
            {loop.map((v, i) => (
              <VideoCard key={`${v.src}-${i}`} video={v} idx={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Page ---------- */

export function CreatorPage() {
  const loginHref = "/login";

  return (
    <div className="cb-root">
      <MarketingNav />

      {/* Hero — pinned to top */}
      <section className="cb-hero-bg">
        <div className="mx-auto w-full max-w-7xl px-5 pt-14 pb-6 text-center sm:px-8 sm:pt-20 sm:pb-8">
          <h1
            data-testid="cb-creator-heading"
            className="cb-display cb-fade-up mx-auto text-[28px] leading-[1.04] text-white sm:whitespace-nowrap sm:text-6xl lg:text-7xl xl:text-[80px]"
          >
            Make <span className="text-[#d8ff2b]">$Money</span> Posting on
          </h1>
          <div className="flex justify-center">
            <RotatingPlatform />
          </div>

          {/* CTAs row */}
          <div className="cb-fade-up mt-10 flex flex-wrap items-center justify-center gap-3.5" style={{ animationDelay: "0.25s" }}>
            <a
              href={DISCORD_INVITE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cb-creator-join-discord-btn"
              className="cb-btn-hover cb-cta group inline-flex items-center gap-2 rounded-full bg-[#5865F2] px-7 py-4 text-base font-bold text-white hover:bg-[#4752c4]"
            >
              <DiscordIcon size={18} />
              Join Creator Network
              <ArrowRight size={18} className="cb-arrow" />
            </a>

            <span
              data-testid="cb-creator-live-counter"
              className="inline-flex items-center gap-2 rounded-full border border-[#1f1f22] bg-[#0a0a0c] px-5 py-3 text-sm font-semibold text-zinc-200"
            >
              <span className="cb-live-dot" />
              +1,170 Live Community
            </span>
          </div>
        </div>
      </section>

      <VideoSlider />

      {/* Ready to start earning card */}
      <section className="bg-[#050505] py-16 sm:py-24">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <div
            data-testid="cb-creator-earn-card"
            className="relative overflow-hidden rounded-3xl border border-[#1f1f22] p-10 text-center sm:p-14"
            style={{
              background:
                "linear-gradient(115deg, rgba(60, 60, 64, 0.35) 0%, rgba(20, 20, 22, 0.6) 45%, rgba(216, 255, 43, 0.10) 100%)",
            }}
          >
            <div className="relative">
              <h2 className="cb-display text-3xl text-white sm:text-5xl">Ready to Start Earning?</h2>
              <p className="mx-auto mt-3 max-w-xl text-base text-[#8a8a90]">
                Sign up, start clipping, get paid. Need guidance? Hop into our Discord community.
              </p>

              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <a
                  href={loginHref}
                  data-testid="cb-creator-signup-btn"
                  className="cb-btn-hover cb-cta cb-cta-equal group inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-3.5 text-sm font-bold text-black hover:bg-zinc-100"
                >
                  Sign Up
                  <ArrowRight size={16} className="cb-arrow" />
                </a>
                <a
                  href={loginHref}
                  data-testid="cb-creator-login-btn"
                  className="cb-btn-hover cb-cta cb-cta-equal group inline-flex items-center justify-center gap-2 rounded-2xl bg-[#d8ff2b] px-6 py-3.5 text-sm font-bold text-black hover:bg-[#c5ee1a]"
                >
                  Login
                  <ArrowRight size={16} className="cb-arrow" />
                </a>
              </div>

              <a
                href={DISCORD_INVITE}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cb-creator-discord-help-link"
                className="mt-6 inline-flex items-center gap-2 text-xs text-[#8a8a90] hover:text-[#5865F2]"
              >
                <DiscordIcon size={13} />
                Need Help? Join Discord community for guidance
              </a>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

export default CreatorPage;
